/* eslint-disable no-case-declarations */
import {
    Asset,
    ASSET_TYPE,
    Episode,
    getIsNewsEpisode,
    resolveSeriesTypeToFetch,
    Season,
    Series,
} from '@24i/nxg-sdk-photon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useContentData } from '@24i/nxg-sdk-smartott-shared/src/context/ContentData';
import { useEffectWithCancelledFlag } from '@24i/nxg-sdk-smartott-shared/src/hooks/useEffectWithCancelledFlag';
import { ISeason, ISeriesData } from '@24i/nxg-sdk-smartott-shared/src/types';

interface ISeasonsState {
    seriesId?: string;
    seasons?: ISeason[];
}

type AnyAsset = Asset | Episode | Series | undefined | null;

export const useSeriesDataManager = (asset: AnyAsset): ISeriesData => {
    const contentDataClient = useContentData();
    const [series, setSeries] = useState<Series>();
    const [seasonsState, setSeasons] = useState<ISeasonsState>({
        seriesId: undefined,
        seasons: undefined,
    });
    const { seasons } = seasonsState;
    const [currentEpisode, setCurrentEpisode] = useState<Episode | undefined>();
    const [nextEpisode, setNextEpisode] = useState<Episode | undefined>();
    const assetAsEpisode = asset as Episode;
    const { t } = useTranslation(['sott']);

    const seriesId =
        asset?.type === ASSET_TYPE.SERIES || asset?.type === ASSET_TYPE.NEWS_SERIES
            ? asset.id
            : assetAsEpisode?.seriesId || assetAsEpisode?.series;
    const attributionId = asset?.attributionId;

    useEffectWithCancelledFlag(
        async ({ getIsCancelled }) => {
            setSeries(undefined);
            setSeasons({
                seriesId: undefined,
                seasons: undefined,
            });
            setCurrentEpisode(undefined);
            setNextEpisode(undefined);

            let finalSeries: Series;
            let finalSeasons: ISeason[] | undefined;
            let finalCurrentEpisode: Episode | undefined;
            let finalNextEpisode: Episode | undefined;
            let rawSeasons: Season[];

            if (!asset || asset?.isTrailer) return;

            switch (asset.type) {
                case ASSET_TYPE.SERIES:
                    rawSeasons = await contentDataClient.fetchEpisodes(seriesId, t);
                    if (getIsCancelled()) {
                        return;
                    }

                    // Find first and second episode
                    if (rawSeasons.length && rawSeasons[0].episodes.length) {
                        [finalCurrentEpisode, finalNextEpisode] = rawSeasons[0].episodes;
                    }

                    finalSeries = asset as Series;
                    setSeries(finalSeries);
                    finalSeasons = rawSeasons.map((season: Season) => ({
                        name: season.name,
                        episodes: season.episodes.map((episode) => ({
                            ...episode,
                            attributionId,
                        })),
                        isCurrent: !!season.episodes.find(
                            (episode: Episode) => episode.id === finalCurrentEpisode?.id
                        ),
                    }));
                    setSeasons({
                        seriesId,
                        seasons: finalSeasons,
                    });
                    break;
                case ASSET_TYPE.EPISODE:
                    const obtainSerieData = async () => {
                        const canObtainSeriesData =
                            assetAsEpisode.seriesId || typeof assetAsEpisode.series === 'string';

                        if (seriesId && seriesId === series?.id) {
                            setSeries(series);
                            return;
                        }
                        if (canObtainSeriesData) {
                            const serieData = await contentDataClient.fetchAsset({
                                id: seriesId,
                                type: resolveSeriesTypeToFetch(asset),
                            });

                            if (getIsCancelled()) {
                                return;
                            }
                            setSeries(serieData as Series);
                        }
                    };

                    const obtainEpisodes = async () => {
                        if (seriesId && seriesId === seasonsState.seriesId) {
                            finalSeasons = seasonsState.seasons?.map((season) => ({
                                name: season.name,
                                episodes: season.episodes.map((episode) => ({
                                    ...episode,
                                    attributionId,
                                })),
                                isCurrent: !!season.episodes.find(
                                    (episode) => episode.id === asset.id
                                ),
                            }));
                            setSeasons({
                                seriesId,
                                seasons: finalSeasons,
                            });
                            return;
                        }
                        if (seriesId) {
                            const seasonsData = await contentDataClient.fetchEpisodes(seriesId, t);
                            if (getIsCancelled()) {
                                return;
                            }
                            finalSeasons = seasonsData.map((season: Season) => ({
                                name: season.name,
                                episodes: season.episodes.map((episode) => ({
                                    ...episode,
                                    attributionId,
                                })),
                                isCurrent: !!season.episodes.find(
                                    (episode: Episode) => episode.id === asset.id
                                ),
                            }));
                            setSeasons({
                                seriesId,
                                seasons: finalSeasons,
                            });
                        }
                    };

                    await Promise.all([obtainSerieData(), obtainEpisodes()]);
                    if (getIsCancelled()) {
                        return;
                    }

                    finalCurrentEpisode = asset as Episode;

                    if (getIsNewsEpisode(asset)) {
                        // no next episode for news - it should navigate back to the news series detail
                        finalNextEpisode = undefined;
                        break;
                    }

                    const currentSeasonIndex =
                        finalSeasons?.findIndex((season) => season.isCurrent) ?? -1;
                    const currentSeason = finalSeasons?.[currentSeasonIndex];

                    const nextSeason = finalSeasons?.[currentSeasonIndex + 1];
                    const currentSeasonEpisodes = currentSeason?.episodes || [];
                    const isLastEpisode =
                        !!currentSeason &&
                        currentSeasonEpisodes[currentSeasonEpisodes.length - 1].episodeNumber ===
                            finalCurrentEpisode.episodeNumber;

                    if (isLastEpisode) {
                        /*
                         * If current episode is the last episode of a season, pick
                         * a first episode of the next season.
                         */
                        if (nextSeason) {
                            const [firstEpisode] = nextSeason.episodes;
                            finalNextEpisode = firstEpisode;
                        }
                    } else {
                        const nextEpisodeNumber = (finalCurrentEpisode.episodeNumber || 0) + 1;
                        finalNextEpisode = currentSeasonEpisodes.find(
                            (episode) =>
                                episode.episodeNumber && episode.episodeNumber >= nextEpisodeNumber
                        );
                    }
                    break;
                default:
                    break;
            }

            setCurrentEpisode(finalCurrentEpisode);
            setNextEpisode(finalNextEpisode);
        },
        [asset]
    );

    return {
        series,
        seasons,
        currentEpisode,
        nextEpisode,
    };
};

export const getEpisodeSubtitle = (asset: Asset, seriesData: ISeriesData) => {
    const { seasonNumber, episodeNumber, title } = asset;
    let episodeSubtitle;
    if (asset.type === ASSET_TYPE.EPISODE) {
        if (seasonNumber && episodeNumber) {
            episodeSubtitle = i18next.t('asset.series.episode.number', {
                seasonNumber,
                episodeNumber,
                episodeTitle: title,
            });
        } else if (seriesData) {
            episodeSubtitle = asset.title;
        }
    }
    return episodeSubtitle;
};
